import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import BookModal from './BookModal';

function resolveAccent(series) {
    if (series.accent) return 'accent-' + series.accent;
    const match = (series.accentColor || '').match(/bg-(\w+)-/);
    const map = {
        emerald: 'emerald', blue: 'sky', violet: 'violet', amber: 'amber',
        rose: 'rose', cyan: 'teal', indigo: 'violet', red: 'rose',
        teal: 'teal', orange: 'amber', pink: 'pink',
    };
    return 'accent-' + (map[match?.[1]] || 'sky');
}

function statusClass(stageOrStatus) {
    const v = (stageOrStatus || '').toLowerCase();
    if (v === 'final') return 'status-final';
    if (v.includes('draft')) return 'status-draft-1';
    if (v === 'coming soon') return 'status-coming-soon';
    if (v) return 'status-work-in-progress';
    return '';
}

function bookStatusClass(book) {
    if (book.loginRequired || book.gated) return 'status-login-required';
    return statusClass(book.stage || book.status);
}

function bookStatusLabel(book) {
    if (book.loginRequired || book.gated) return 'Login required';
    if (book.comingSoon) return 'Coming soon';
    return book.stage || book.status || '';
}

function fmtUpdated(iso) {
    if (!iso) return '';
    const [y, m] = iso.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[Number(m) - 1]} ${y}`;
}

export default function BookSeriesModal({ isOpen, onClose, series, isGuest = false, onSignIn }) {
    const [selectedBook, setSelectedBook] = useState(null);
    const [isBookModalOpen, setIsBookModalOpen] = useState(false);

    if (!series) return null;

    const accentCls = resolveAccent(series);
    const stage = series.status || series.stage || '';
    const books = series.books || [];

    const handleClose = () => {
        setSelectedBook(null);
        setIsBookModalOpen(false);
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className="themed-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={handleClose}
                    />
                    <motion.div
                        className={`themed-modal ${accentCls}`}
                        initial={{ opacity: 0, scale: 0.97, x: '-50%', y: 'calc(-50% + 16px)' }}
                        animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
                        exit={{ opacity: 0, scale: 0.97, x: '-50%', y: 'calc(-50% + 16px)' }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                        <div className="themed-modal-accent-bar" />
                        <div className="themed-modal-body">
                            <button
                                className="themed-modal-close"
                                onClick={handleClose}
                                aria-label="Close"
                            >
                                ✕
                            </button>

                            <div className="themed-modal-hd-row">
                                <div className="themed-modal-icon">
                                    {series.title.charAt(0)}
                                </div>
                                <div>
                                    <h2 className="themed-modal-title">{series.title}</h2>
                                    <div className="themed-modal-subtitle">{series.genre}</div>
                                </div>
                            </div>

                            <div className="themed-modal-meta-row">
                                {stage && (
                                    <span className={'status-pill ' + statusClass(stage)}>{stage}</span>
                                )}
                                {series.lastUpdated && (
                                    <span className="themed-modal-meta-item">
                                        Updated {fmtUpdated(series.lastUpdated)}
                                    </span>
                                )}
                                <span className="themed-modal-meta-item">
                                    {series.count ?? books.length}&nbsp;
                                    {(series.count ?? books.length) === 1 ? 'book' : 'books'}
                                </span>
                            </div>

                            <p className="themed-modal-section-label">About the Series</p>
                            <p className="themed-modal-text">{series.blurb || series.description}</p>

                            <p className="themed-modal-section-label">Books in this Series</p>
                            {books.length > 0 ? (
                                <ul className="modal-book-list">
                                    {books.map((book, idx) => (
                                        <li key={book.id || idx}>
                                            <button
                                                className="modal-book-btn"
                                                onClick={() => {
                                                    setSelectedBook(book);
                                                    setIsBookModalOpen(true);
                                                }}
                                            >
                                                <span className="modal-book-num">{idx + 1}</span>
                                                <span className="modal-book-title">{book.title}</span>
                                                <span className={'status-pill ' + bookStatusClass(book)}>
                                                    {bookStatusLabel(book)}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p style={{ color: 'var(--ink-3)', fontSize: 13, fontStyle: 'italic', marginBottom: 24 }}>
                                    No books yet — check back soon.
                                </p>
                            )}
                        </div>
                    </motion.div>

                    <BookModal
                        isOpen={isBookModalOpen}
                        onClose={() => setIsBookModalOpen(false)}
                        book={selectedBook}
                        series={series}
                        isGuest={isGuest}
                        onSignIn={() => { setIsBookModalOpen(false); onClose(); onSignIn?.(); }}
                    />
                </>
            )}
        </AnimatePresence>
    );
}
